import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { Mail, CheckCircle, Eye, EyeOff, KeyRound } from "lucide-react";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getCanonicalUrl } from "@/config/site";
import { useRequestGenieAccess, useUnlockGenieWithPassword } from "@/hooks/agent/useAgentPage";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const MAGENTA_BEAM = "#FF4DB8";
const SPARK_BLUE = "#4E9CFF";

interface Props {
  urlName: string;
  genieName: string;
  passwordProtected?: boolean;
}

interface FormValues {
  email: string;
}

interface PasswordFormValues {
  password: string;
}

const readApiErrorMessage = (error: unknown) => {
  const apiError = error && typeof error === "object" ? Reflect.get(error, "apiError") : null;
  const message = apiError && typeof apiError === "object" ? Reflect.get(apiError, "message") : null;
  return typeof message === "string" ? message : "That password didn't work. Please try again.";
};

const GenieAccessRestrictedPage = ({ urlName, genieName, passwordProtected = false }: Props) => {
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { mutate: requestAccess, isPending } = useRequestGenieAccess();
  const unlock = useUnlockGenieWithPassword();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();
  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
  } = useForm<PasswordFormValues>();

  const onSubmit = ({ email }: FormValues) => {
    requestAccess({ urlName, email }, { onSuccess: () => setSubmitted(true) });
  };

  const onUnlock = ({ password }: PasswordFormValues) => {
    unlock.mutate({ urlName, password });
  };

  const isUnlocking = unlock.isPending || unlock.isSuccess;

  const displayName = genieName?.trim() || "This genie";

  return (
    <>
      <Helmet>
        <title>{`${displayName} is resting in its lamp | Help Genie`}</title>
        <meta
          name="description"
          content="This genie is private. Ask the owner to summon it from its lamp."
        />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={getCanonicalUrl("/404")} />
      </Helmet>

      {/* Page shell — public marketing-style page (no AppLayout), brand gradient backdrop */}
      <div
        className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${MAGENTA_BEAM}08, white 50%, ${SPARK_BLUE}08)`,
        }}
      >
        {/* Soft ambient wisps */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-40"
          style={{ background: `radial-gradient(closest-side, ${SPARK_BLUE}22, transparent)` }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-40 -right-24 w-[480px] h-[480px] rounded-full blur-3xl opacity-40"
          style={{ background: `radial-gradient(closest-side, ${MAGENTA_BEAM}22, transparent)` }}
        />

        <div className="flex justify-center mb-6 p-2 relative z-10">
          <HelpGenieLogo size={56} />
        </div>

        {/* Card with brand-gradient top bar */}
        <div
          className="w-full max-w-md bg-white rounded-2xl shadow-lg overflow-hidden relative z-10"
          style={{ border: `1px solid ${SPARK_BLUE}20` }}
        >
          <div
            style={{
              height: 4,
              background: `linear-gradient(90deg, ${SPARK_BLUE}, ${MAGENTA_BEAM})`,
            }}
          />

          <div className="p-8 flex flex-col items-center text-center">
            {/* Lamp hero — brand PNG with gentle float/breathe loop */}
            <div
              className="mb-5 p-5 rounded-2xl relative"
              style={{
                background: `linear-gradient(135deg, ${MAGENTA_BEAM}12, ${SPARK_BLUE}12)`,
              }}
            >
              {/* Soft glow behind the lamp */}
              <div
                aria-hidden
                className="absolute inset-0 rounded-2xl blur-xl opacity-60"
                style={{
                  background: `radial-gradient(closest-side, ${MAGENTA_BEAM}25, transparent 70%)`,
                }}
              />
              <GenieIcon
                name="genie-lamp"
                size={80}
                className="genie-lamp-float relative w-16 h-16 md:w-20 md:h-20 select-none pointer-events-none"
                style={{
                  animation: "genie-lamp-float 4.2s ease-in-out infinite",
                }}
              />
            </div>

            <h1 className="text-2xl md:text-[26px] font-bold text-[#222B45] mb-2 leading-tight">
              {genieName ? `${displayName} is resting in its lamp` : "This genie is resting in its lamp"}
            </h1>
            <p className="text-[#64748B] text-base mb-6">
              {passwordProtected
                ? "Speak the magic word the owner shared with you, and it will wake right up."
                : "Only the owner can summon it. Send them a quick note and they'll decide whether to wake it for you."}
            </p>

            {passwordProtected && !showForm && !submitted && (
              <div className="w-full space-y-4">
                <form onSubmit={handlePasswordSubmit(onUnlock)} className="w-full space-y-3">
                  <div className="text-left">
                    <label htmlFor="genie-password" className="text-sm font-semibold text-gray-700 mb-1 block">
                      Password
                    </label>
                    <div className="relative">
                      <KeyRound
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                      />
                      <Input
                        id="genie-password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        autoFocus
                        placeholder="Enter the password"
                        className="pl-9 pr-11 min-h-[44px] text-base sm:text-sm focus-visible:ring-genie-primary"
                        {...registerPassword("password", {
                          required: "Enter the password",
                          onChange: () => unlock.reset(),
                        })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((visible) => !visible)}
                        className="absolute right-0 top-1/2 -translate-y-1/2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {(passwordErrors.password || unlock.isError) && (
                      <p className="text-xs text-red-600 mt-1" role="alert">
                        {passwordErrors.password?.message ?? readApiErrorMessage(unlock.error)}
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    disabled={isUnlocking}
                    className="w-full min-h-[44px] text-white font-semibold py-2.5 shadow-lg shadow-[#4E9CFF]/25 hover:shadow-xl hover:shadow-[#4E9CFF]/35 hover:scale-[1.02] transition-all duration-200"
                    style={{
                      background: `linear-gradient(135deg, ${SPARK_BLUE}, ${MAGENTA_BEAM})`,
                    }}
                  >
                    {isUnlocking ? "Summoning your genie…" : "Summon the genie"}
                  </Button>
                </form>
                <button
                  type="button"
                  onClick={() => setShowForm(true)}
                  className="min-h-[44px] text-sm font-medium text-[#64748B] hover:text-[#222B45] transition-colors"
                >
                  Don't have the password? Ask the owner
                </button>
              </div>
            )}

            {!passwordProtected && !showForm && !submitted && (
              <Button
                onClick={() => setShowForm(true)}
                className="w-full text-white font-semibold py-2.5 shadow-lg shadow-[#4E9CFF]/25 hover:shadow-xl hover:shadow-[#4E9CFF]/35 hover:scale-[1.02] transition-all duration-200"
                style={{
                  background: `linear-gradient(135deg, ${SPARK_BLUE}, ${MAGENTA_BEAM})`,
                }}
              >
                Ask the owner to summon it
              </Button>
            )}

            {showForm && !submitted && (
              <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-3">
                <div className="text-left">
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">
                    Your email
                  </label>
                  <div className="relative">
                    <Mail
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      className="pl-9 focus-visible:ring-genie-primary"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Enter a valid email",
                        },
                      })}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-xs text-red-600 mt-1">{errors.email.message}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    We'll let the owner know you'd like in. No spam, no list.
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={isPending}
                  className="w-full text-white font-semibold py-2.5 shadow-lg shadow-[#4E9CFF]/25 hover:shadow-xl hover:shadow-[#4E9CFF]/35 transition-all duration-200"
                  style={{
                    background: `linear-gradient(135deg, ${SPARK_BLUE}, ${MAGENTA_BEAM})`,
                  }}
                >
                  {isPending ? "Sending the wish…" : "Send the request"}
                </Button>
              </form>
            )}

            {submitted && (
              <div className="flex flex-col items-center gap-3">
                <CheckCircle size={40} style={{ color: SPARK_BLUE }} />
                <p className="text-gray-900 font-semibold">Your wish is on its way.</p>
                <p className="text-[#64748B] text-sm">
                  We've nudged the owner. If they release the lamp, you'll hear back by email.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Keyframes for the lamp's slow float/breathe loop */}
        <style>{`
          @keyframes genie-lamp-float {
            0%, 100% {
              transform: translateY(0px) rotate(-1.5deg);
              filter: drop-shadow(0 8px 14px rgba(78, 156, 255, 0.18));
            }
            50% {
              transform: translateY(-6px) rotate(1.5deg);
              filter: drop-shadow(0 14px 22px rgba(255, 77, 184, 0.22));
            }
          }
          @media (prefers-reduced-motion: reduce) {
            .genie-lamp-float {
              animation: none !important;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default GenieAccessRestrictedPage;
